import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Circle,
  ExternalLink,
  FileText,
  Globe,
  Languages,
  LifeBuoy,
  Lightbulb,
  ListChecks,
  MapPin,
  Megaphone,
  Route,
  ScanLine,
  Sparkles,
  MessageSquareText,
  Upload,
  WalletCards
} from 'lucide-react';
import { getOnboardingTranslations, ONBOARDING_LANGUAGES } from './onboardingTranslations';
import { getLaunchTrainingSteps, getPromotionPlanNotice, localizePromotionTask } from './promotionPlanConfig';
import { getStaffTrainingSteps } from './promotionPlanConfig';
import StaffTrainingGuide from './StaffTrainingGuide';
import ClientMediaPackageView from './ItineraryCreator/mediaPackage/ClientMediaPackageView';
import { MediaLaunchTimeline } from './ItineraryCreator/mediaPackage/MediaLaunchCalendar';
import { db } from '../firebase';

const ONBOARDING_PUBLIC_PAGE_ENDPOINT = `${process.env.REACT_APP_FUNCTIONS_BASE_URL || 'https://us-central1-guidesofia-access-control.cloudfunctions.net'}/getOnboardingPublicPage`;
const ONBOARDING_MARK_REVIEW_ENDPOINT = ONBOARDING_PUBLIC_PAGE_ENDPOINT;
const GUIDE_SOFIA_BAR_COLOR = '#2563eb';
const CLIENT_BAR_COLOR = '#16a34a';
const BOTH_BAR_COLOR = '#0be7d2';
const GUIDE_SOFIA_PAYMENT_DETAILS = {
  entity: 'OBSIDIANBLOOM - Unipessoal Lda',
  addressLine1: 'Rua Manuel Da Silva Leal, n.º 2, 8 esq, 1600-166',
  addressLine2: 'Lisboa, Portugal',
  taxNumber: 'Nº Contribuinte: 518975185',
  email: 'nelio@guidesofia.com',
  bankName: 'Revolut',
  iban: 'LT153250007050588941'
};
const BRAND_CYAN = '#00BEDD';
const BRAND_DEEP = '#012B39';
const BRAND_MID = '#003E47';
const DEFAULT_ONBOARDING_LANGUAGE = 'pt-PT';
const PANEL_CLASS = 'rounded-[24px] border border-white/60 bg-white/92 p-5 shadow-[0_18px_60px_rgba(1,43,57,0.14)] backdrop-blur';
const BUTTON_PRIMARY_CLASS = 'inline-flex items-center justify-center rounded-full bg-slate-950 px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_24px_rgba(1,43,57,0.28)] transition hover:-translate-y-0.5 hover:bg-[#003E47]';
const SCOPE_PRODUCT_PROFILES = {
  essential: { label: 'Essential Guide', idealFor: 'Museums with a ready-to-go script.', pois: 'Up to 15 POIs', words: 'Up to 6,000 words', source: 'Client provides final script', revisions: 'No collaborative script revisions included', refreshes: '0 included content refreshes per year', languages: 'Up to 3 languages', features: ['QR code recognition', 'Geography-based recognition (GPS)', 'Human QA and client approval'] },
  plus: { label: 'Plus Guide', idealFor: 'The complete Storytelling as a Service solution.', pois: 'Up to 20 POIs', words: 'Up to 8,000 words', source: 'GuideSofia creates the script from raw documents', revisions: 'Up to 2 revision rounds of the initial script', refreshes: 'Up to 2 revision rounds per year on up to 3 POIs', languages: 'Up to 4 languages', features: ['QR code recognition', 'Geography-based recognition (GPS)', 'Visitor analytics dashboard', 'Human QA and client approval'] },
  premium: { label: 'Premium AI', idealFor: 'Flagship institutions seeking the ultimate personalized visitor experience.', pois: 'Up to 25 POIs', words: 'Up to 10,000 words', source: 'GuideSofia creates the script from raw documents', revisions: 'Up to 2 revision rounds of the initial script', refreshes: 'Up to 3 revision rounds per year on up to 5 POIs', languages: 'Up to 7 languages', features: ['QR code recognition', 'Geography-based recognition (GPS)', 'Conversational AI chatbot', 'Visitor analytics dashboard', 'Personalized AI itineraries', 'Human QA and client approval'] }
};

const STAGES = [
  { id: 'kickoffMeeting', step: '00', title: 'Kickoff Meeting', owner: 'Client + GuideSofia', defaultDurationDays: 1, color: BOTH_BAR_COLOR, dependsOn: [] },
  { id: 'contract', step: '01', title: 'Contract review + 50% Deposit', owner: 'Client + GuideSofia', defaultDurationDays: 3, color: BOTH_BAR_COLOR, dependsOn: ['kickoffMeeting'] },
  { id: 'kickoff', step: '02', title: 'Content Meeting', owner: 'Client + GuideSofia', defaultDurationDays: 1, color: BOTH_BAR_COLOR, dependsOn: ['contract'] },
  { id: 'routeMaterials', step: '03', title: 'Route / Stops + Materials', owner: 'Client', defaultDurationDays: 7, color: CLIENT_BAR_COLOR, dependsOn: ['kickoff'] },
  { id: 'draft', step: '04', title: 'First version of the script', owner: 'GuideSofia', defaultDurationDays: 5, color: GUIDE_SOFIA_BAR_COLOR, dependsOn: ['routeMaterials'] },
  { id: 'review', step: '06', title: 'Client Review', owner: 'Client', defaultDurationDays: 5, color: CLIENT_BAR_COLOR, dependsOn: ['draft'] },
  { id: 'translations', step: '07', title: 'Translations + Voice Overs', owner: 'GuideSofia', defaultDurationDays: 3, color: GUIDE_SOFIA_BAR_COLOR, dependsOn: ['review'] },
  { id: 'qa', step: '08', title: 'Quality control (Humano)', owner: 'GuideSofia', defaultDurationDays: 5, color: GUIDE_SOFIA_BAR_COLOR, dependsOn: ['review'] },
  { id: 'midwork', step: '09', title: 'Ponto de situação intermédio', owner: 'Client + GuideSofia', defaultDurationDays: 1, color: BOTH_BAR_COLOR, dependsOn: ['qa', 'translations'] },
  { id: 'launch', step: '10', title: 'Launch Communication', owner: 'Client + GuideSofia', defaultDurationDays: 2, color: BOTH_BAR_COLOR, dependsOn: ['review'] }
];

const GUIDE_LANGUAGE_OPTIONS = [
  { value: 'pt-PT', label: 'Portuguese (PT-PT)' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
  { value: 'ru', label: 'Russian' },
  { value: 'ar', label: 'Arabic' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ja', label: 'Japanese' }
];

const MATERIAL_ITEMS = [
  { id: 'institution', label: 'Institution / project name', icon: Globe },
  { id: 'contact', label: 'Main contact person', icon: FileText },
  { id: 'objective', label: 'Project objective', icon: LifeBuoy },
  { id: 'texts', label: 'Texts / docs / PDFs', icon: FileText },
  { id: 'media', label: 'Photos / audio / media', icon: Upload },
  { id: 'venue', label: 'Venue / project context', icon: Globe },
  { id: 'route', label: 'Stops list / route indication', icon: Route },
  { id: 'gps', label: 'GPS points', icon: MapPin },
  { id: 'brand', label: 'Brand guidelines / logo', icon: Globe },
  { id: 'accessibility', label: 'Accessibility needs', icon: LifeBuoy },
  { id: 'launchDate', label: 'Launch date or ideal timeline', icon: CalendarDays }
];

const PAYMENT_ITEMS = [
  { id: 'contractReviewed', label: 'Contract reviewed', type: 'contract' },
  { id: 'contractSigned', label: 'Contract signed', type: 'contract' },
  { id: 'depositPaid', label: '50% deposit paid at signing', type: 'payment' },
  { id: 'finalPaid', label: 'Final 50% paid at guide delivery', type: 'payment' }
];

const STATUS_CHIP_CLASSES = {
  todo: 'bg-slate-200 text-slate-700',
  active: 'bg-amber-100 text-amber-800',
  blocked: 'bg-rose-100 text-rose-800',
  done: 'bg-emerald-100 text-emerald-800'
};

const PROJECT_TYPES = [
  { value: 'museum', label: 'Museum Guide' },
  { value: 'municipality', label: 'Municipality / Destination' },
  { value: 'temporary', label: 'Temporary Exhibition' },
  { value: 'pilot', label: 'Pilot Project' },
  { value: 'virtual', label: 'Virtual Tour / 360' }
];

const ADD_ONS = [
  { id: 'venue-pages', label: 'Venue pages', description: 'Dedicated venue or exhibition landing pages connected to the guide.', icon: Globe },
  { id: 'qr-rollout', label: 'QR rollout', description: 'Extra support for QR implementation, placement planning and rollout.', icon: ScanLine },
  { id: 'translations', label: 'Extra languages', description: 'Additional translations and voice overs beyond the base scope.', icon: Languages },
  { id: 'virtual-360', label: 'Virtual 360 visit', description: 'A 360-degree virtual experience for remote or hybrid visits.', icon: Globe },
  { id: 'augmented-reality', label: 'Augmented reality', description: 'AR moments that add digital layers to the visitor experience.', icon: Sparkles },
  { id: 'gamification', label: 'Gamification', description: 'Quizzes, challenges or playful interactions for visitors.', icon: Sparkles },
  { id: 'sign-language-pt', label: 'Portuguese sign language', description: 'Accessibility support with Portuguese sign language content.', icon: Languages },
  { id: 'collection-catalog', label: 'Collection catalog', description: 'A digital catalog for the collection or exhibition highlights.', icon: FileText },
  { id: 'surveys', label: 'Surveys', description: 'Visitor surveys or feedback forms connected to the project.', icon: FileText },
  { id: 'advanced-content', label: 'Advanced content', description: 'Richer storytelling, special features or expanded content modules.', icon: Sparkles }
];

const HUB_SECTIONS = [
  { id: 'timeline', label: 'Timeline' },
  { id: 'production', label: 'Production' },
  { id: 'review', label: 'Review' },
  { id: 'launch', label: 'Launch' },
  { id: 'add-ons', label: 'Optional add-ons' },
  { id: 'contact', label: 'Contact' },
  { id: 'project-details', label: 'Project details' },

];

const PRODUCTION_SUBSECTIONS = [
  { id: 'materials', label: 'Materials' },
  { id: 'languages', label: 'Languages' }
];

const LAUNCH_SUBSECTIONS = [
  { id: 'qr-codes', label: 'QR Codes', icon: ScanLine },
  { id: 'media-press', label: 'Media Package', icon: Megaphone },
  { id: 'launch', label: 'Launch plan' },
  { id: 'launch-training', label: 'Training' }
];

const PROJECT_SUBSECTIONS = [
  { id: 'documents', label: 'Documents' },
  { id: 'payments', label: 'Payments' },
  { id: 'scope', label: 'Scope & changes' },
  { id: 'roles', label: 'Responsibilities' },
  { id: 'faq', label: 'FAQ' }
];

const DEFAULT_PROMOTION_TASKS = [
  ['teaser', 'before', 'Publish a teaser for the new digital visitor experience', 'Give visitors a reason to look forward to the guide before launch.', 'Social media'],
  ['internal-communication', 'before', 'Tell all relevant teams about the launch', 'Brief reception, visitor services, education, communications and management.', 'Internal email / meeting'],
  ['newsletter-event', 'before', 'Prepare and schedule the email or newsletter communication for launch week', 'Prepare one clear message before launch and schedule it for the week the guide becomes available.', 'Email / newsletter'],
  ['website', 'before', 'Prepare the website visitor-information update', 'Place the guide where visitors look for opening hours, tickets and practical information.', 'Website'],
  ['launch-website', 'launch-day', 'Publish the website announcement', 'Make the guide visible on the official website on launch day.', 'Website'],
  ['launch-social', 'launch-day', 'Publish the launch social post or story', 'Show the guide in use and give people one clear action.', 'Social media'],
  ['signage', 'launch-day', 'Activate on-site signage', 'Place clear QR signs where visitors pause and understand the benefit.', 'On site'],
  ['staff-introduction', 'launch-day', 'Have staff actively introduce the guide', 'A confident recommendation from staff often matters more than another poster.', 'Visitor interaction'],
  ['launch-content', 'launch-day', 'Capture a photo or short video showing the guide in use', 'Use real launch-day material for later reminders and learning.', 'Photo / video'],
  ['reminder', 'after', 'Publish a reminder post', 'Repeat the invitation during the first two weeks.', 'Social media'],
  ['featured-story', 'after', 'Highlight one interesting story from the guide', 'Show the value of the guide instead of only announcing it exists.', 'Social / website'],
  ['access-video', 'after', 'Share a short video showing the app in use', 'Show visitors how to start the guide and what they can discover during the visit.', 'Social / website'],
  ['feedback-highlight', 'after', 'Share visitor feedback or an early usage highlight', 'Use real observations to make the experience credible and useful.', 'Social / newsletter'],
  ['seasonal-promotion', 'after', 'Connect the guide to an event, weekend or seasonal campaign', 'Attach the guide to an existing reason to visit.', 'Event / campaign']
];

const createDefaultPromotionPlan = () => ({
  enabled: true,
  clientInstructions: 'Introduce the guide repeatedly across the visitor journey. Use the prepared copy and materials for each activity, then check it off when complete.',
  tasks: DEFAULT_PROMOTION_TASKS.map(([id, phase, title, guidance, channel]) => ({ id, phase, title, guidance, example: '', channel, completed: false }))
});

const resolvePromotionCopy = (value, client) => {
  const venue = client?.name || client?.guideName || 'your venue';
  const launchDate = client?.agreedLaunchDate ? formatDate(new Date(`${client.agreedLaunchDate}T00:00:00`), 'en-GB') : 'the launch date';
  const selectedLanguages = Object.entries(client?.guideLanguages || {})
    .filter(([, state]) => state?.selected)
    .map(([code]) => code)
    .join(', ');

  return String(value || '')
    .replaceAll('[venue]', venue)
    .replaceAll('[date]', launchDate)
    .replaceAll('[languages]', selectedLanguages || 'the available languages')
    .replaceAll('[link]', 'the guide link')
    .replaceAll('[object / place]', 'a featured stop')
    .replaceAll('[language]', 'a selected language')
    .replaceAll('[stop / route]', 'the route')
    .replaceAll('[event]', 'your next event');
};

const LAUNCH_ITEMS = [
  { id: 'contentApproved', label: 'Content approved' },
  { id: 'translationsApproved', label: 'Translations approved' },
  { id: 'audioChecked', label: 'Audio checked' },
  { id: 'qrTested', label: 'QR codes tested' },
  { id: 'qrPlaced', label: 'Physical QR codes placed' },
  { id: 'teamInformed', label: 'Reception / visitor team informed' },
  { id: 'websiteReady', label: 'Website or social post prepared' },
  { id: 'analyticsActive', label: 'Analytics activated' },
  { id: 'launchConfirmed', label: 'Launch date confirmed' }
];

const LAUNCH_RESPONSIBILITIES = [
  { id: 'qrPlacement', label: 'Place QR codes on site' },
  { id: 'teamTraining', label: 'Train reception / visitor team and share the staff training document' },
  { id: 'onlinePromotion', label: 'Publish website and social communication' },
  { id: 'onsiteTest', label: 'Run the final on-site test' },
  { id: 'launchSignoff', label: 'Confirm public launch' }
];

const LAUNCH_ASSET_ITEMS = [
  { id: 'qrCodeLinks', label: 'QR code links' },
  { id: 'promoMaterialsLinks', label: 'Promotion materials links' }
];

const MEDIA_PRESS_ITEMS = [
  { id: 'mediaKitUrl', label: 'Media kit link' },
  { id: 'pressReleaseUrl', label: 'Press release link' },
  { id: 'pressCoverageLinks', label: 'Press coverage links' }
];

const REVIEW_ITEMS = [
  { id: 'clientGuideText', label: 'Guide text reviewed', owner: 'client' },
  { id: 'qaTexts', label: 'Texts quality control (Humano)', owner: 'guidesofia' },
  { id: 'qaQrCodes', label: 'QR codes quality control (Humano)', owner: 'guidesofia' },
  { id: 'qaAudios', label: 'Audio quality control (Humano)', owner: 'guidesofia' }
];

const QR_PLACEMENTS = [
  { id: 'reception', label: 'Reception / ticket office' },
  { id: 'routeStart', label: 'Start of route' },
  { id: 'stops', label: 'Each room, stop or object area' },
  { id: 'printed', label: 'Printed maps / brochures' },
  { id: 'website', label: 'Website' },
  { id: 'social', label: 'Social media / newsletter' }
];

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const toStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const toBusinessDay = (date) => {
  const next = toStartOfDay(date);
  while (isWeekend(next)) {
    next.setDate(next.getDate() + 1);
  }
  return next;
};

const addBusinessDays = (date, businessDays) => {
  const next = toBusinessDay(date);
  let remaining = Math.abs(businessDays);
  const direction = businessDays >= 0 ? 1 : -1;

  while (remaining > 0) {
    next.setDate(next.getDate() + direction);
    if (!isWeekend(next)) {
      remaining -= 1;
    }
  }

  return next;
};

const countBusinessDaysInclusive = (startDate, endDate) => {
  let count = 0;
  let cursor = toStartOfDay(startDate);
  const end = toStartOfDay(endDate);

  while (cursor <= end) {
    if (!isWeekend(cursor)) {
      count += 1;
    }
    cursor = addDays(cursor, 1);
  }

  return count;
};

const diffDays = (startDate, endDate) => {
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  return Math.round((end - start) / 86400000);
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const parseLabeledLink = (line) => {
  const trimmed = (line || '').trim();
  if (!trimmed) return null;

  const separatorIndex = trimmed.indexOf('|');
  if (separatorIndex === -1) {
    return { label: trimmed, url: trimmed };
  }

  const label = trimmed.slice(0, separatorIndex).trim();
  const url = trimmed.slice(separatorIndex + 1).trim();

  if (!url) {
    return { label: trimmed, url: trimmed };
  }

  return {
    label: label || url,
    url
  };
};

const parseContactLines = (value) => String(value || '')
  .split(/\r?\n/)
  .map((line) => line.trim())
  .filter(Boolean)
  .map((line) => {
    const [name = '', email = '', phone = ''] = line.split('|').map((part) => part.trim());
    return { name, email, phone };
  });

const formatDate = (value, locale) => (
  value instanceof Date && !Number.isNaN(value.getTime())
    ? new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(value)
    : ''
);

const buildTimelineStages = (stages, kickoffDate, stageDurations, stageStatuses) => {
  const baseDate = toBusinessDay(kickoffDate ? new Date(`${kickoffDate}T09:00:00`) : new Date());
  const computedStages = new Map();

  stages.forEach((stage) => {
    const rawDuration = Number(stageDurations?.[stage.id]);
    const durationDays = Number.isFinite(rawDuration) ? Math.max(0, rawDuration) : stage.defaultDurationDays;
    const dependencyCompletionDates = (stage.dependsOn || [])
      .map((dependencyId) => computedStages.get(dependencyId)?.completionDate)
      .filter(Boolean);
    const startDate = stage.id === 'routeMaterials' && computedStages.get('kickoff')
      ? new Date(computedStages.get('kickoff').startDate)
      : stage.id === 'midwork' && computedStages.get('qa')
      ? addBusinessDays(computedStages.get('qa').endDate, -1)
      : dependencyCompletionDates.length > 0
      ? addBusinessDays(new Date(Math.max(...dependencyCompletionDates.map((value) => value.getTime()))), 1)
      : new Date(baseDate);
    const launchQualityStage = stage.id === 'launch' ? computedStages.get('qa') : null;
    const endDate = launchQualityStage
      ? addBusinessDays(launchQualityStage.endDate, durationDays)
      : durationDays === 0 ? new Date(startDate) : addBusinessDays(startDate, durationDays - 1);
    const completionDate = launchQualityStage
      ? new Date(endDate)
      : durationDays === 0 ? addBusinessDays(startDate, -1) : new Date(endDate);
    const displayedDurationDays = launchQualityStage ? diffDays(startDate, endDate) + 1 : durationDays;

    computedStages.set(stage.id, {
      ...stage,
      status: stageStatuses?.[stage.id] || 'todo',
      durationDays: displayedDurationDays,
      startDate,
      endDate,
      completionDate
    });
  });

  return stages.map((stage) => computedStages.get(stage.id));
};

const getGuideLanguageState = (client, languageCode) => {
  const state = client?.guideLanguages?.[languageCode] || {};
  const isBaseLanguage = languageCode === client?.baseLanguage;

  return {
    selected: Boolean(state.selected || isBaseLanguage),
    ready: Boolean(state.ready || isBaseLanguage)
  };
};

const ClientOnboardingPublicPage = ({ clientId }) => {
  const [client, setClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasEnteredHub, setHasEnteredHub] = useState(false);
  const [showReviewConfirm, setShowReviewConfirm] = useState(false);
  const initialHubSection = useMemo(() => {
    const section = new URLSearchParams(window.location.search).get('section') || 'timeline';
    const normalizedSection = ['launch-package', 'after-launch', 'launch-promotion', 'launch-assets'].includes(section)
      ? 'launch'
      : section === 'project-details'
        ? 'documents'
        : section === 'production'
          ? 'materials'
        : section;
    return ['timeline', 'materials', 'languages', 'review', 'launch', 'launch-promotion', 'add-ons', 'launch-assets', 'qr-codes', 'media-press', 'documents', 'payments', 'scope', 'launch-training', 'contact', 'roles', 'faq'].includes(normalizedSection)
      ? normalizedSection
      : 'timeline';
  }, []);
  const [activeHubSection, setActiveHubSection] = useState(initialHubSection);
  const [language, setLanguage] = useState(DEFAULT_ONBOARDING_LANGUAGE);
  const [isMarkingReviewText, setIsMarkingReviewText] = useState(false);
  const [reviewTextStatus, setReviewTextStatus] = useState('');
  const [promotionUpdateTaskId, setPromotionUpdateTaskId] = useState('');
  const [promotionUpdateStatus, setPromotionUpdateStatus] = useState('');
  const [promotionPhase, setPromotionPhase] = useState('before');
  const [expandedPromotionTasks, setExpandedPromotionTasks] = useState({});
  const [expandedTrainingItems, setExpandedTrainingItems] = useState({});
  const accessToken = useMemo(() => new URLSearchParams(window.location.search).get('access') || '', []);
  const languageStorageKey = useMemo(() => `onboarding-language-${clientId}`, [clientId]);

  useEffect(() => {
    localStorage.setItem(languageStorageKey, language);
  }, [language, languageStorageKey]);

  const content = useMemo(() => getOnboardingTranslations(language), [language]);
  const showContractSteps = (client?.showContractSteps ?? true) !== false;
  const showPaymentSteps = (client?.showPaymentSteps ?? client?.showPayments ?? true) !== false;
  const translatedStages = useMemo(() => STAGES.map((stage) => ({
    ...stage,
    title: stage.id === 'contract'
      ? showContractSteps && showPaymentSteps
        ? (content.payments.contractPaymentStage || content.stages[stage.id]?.title || stage.title)
        : showContractSteps
          ? (content.payments.contractStage || content.stages[stage.id]?.title || stage.title)
          : (content.payments.paymentStage || content.stages[stage.id]?.title || stage.title)
      : (content.stages[stage.id]?.title || stage.title),
    owner: content.stages[stage.id]?.owner || stage.owner
  })), [content, showContractSteps, showPaymentSteps]);
  const translatedMaterials = useMemo(() => MATERIAL_ITEMS.map((item) => ({
    ...item,
    label: content.materials[item.id] || item.label
  })), [content]);
  const translatedPaymentItems = useMemo(() => PAYMENT_ITEMS.map((item) => ({
    ...item,
    label: content.payments[item.id] || item.label
  })), [content]);
  const translatedProjectTypes = useMemo(() => PROJECT_TYPES.map((type) => ({
    ...type,
    label: content.projectTypes[type.value] || type.label
  })), [content]);
  const translatedAddOns = useMemo(() => ADD_ONS.map((item) => ({
    ...item,
    label: content.addOns?.[item.id]?.label || item.label,
    description: content.addOns?.[item.id]?.description || item.description
  })), [content]);
  const translatedHubSections = useMemo(() => HUB_SECTIONS.map((section) => ({
    ...section,
    label: content.tabs[section.id] || section.label
  })), [content]);
  const visibleHubSections = useMemo(() => (
    translatedHubSections.filter((section) => section.id !== 'payments' || showPaymentSteps)
  ), [showPaymentSteps, translatedHubSections]);
  const translatedLaunchItems = useMemo(() => LAUNCH_ITEMS.map((item) => ({
    ...item,
    label: content.launchItems[item.id] || item.label
  })), [content]);
  const translatedLaunchAssets = useMemo(() => LAUNCH_ASSET_ITEMS.map((item) => ({
    ...item,
    label: content.launchAssets[item.id] || item.label
  })), [content]);
  const translatedMediaPressItems = useMemo(() => MEDIA_PRESS_ITEMS.map((item) => ({
    ...item,
    label: content.mediaPress?.[item.id] || item.label
  })), [content]);
  const translatedReviewItems = useMemo(() => REVIEW_ITEMS.map((item) => ({
    ...item,
    label: content.reviewItems[item.id] || item.label
  })), [content]);
  const translatedQrPlacements = useMemo(() => QR_PLACEMENTS.map((item) => ({
    ...item,
    label: content.qrPlacements[item.id] || item.label
  })), [content]);
  const guideSofiaAdditionalContacts = useMemo(() => parseContactLines(client?.projectManagerAdditionalContacts), [client?.projectManagerAdditionalContacts]);
  const clientAdditionalContacts = useMemo(() => parseContactLines(client?.clientAdditionalContacts), [client?.clientAdditionalContacts]);

  useEffect(() => {
    let cancelled = false;

    const loadClient = async () => {
      if (!accessToken) {
        setError('This onboarding page is not available.');
        setIsLoading(false);
        return;
      }

      try {
        const url = new URL(ONBOARDING_PUBLIC_PAGE_ENDPOINT);
        url.searchParams.set('clientId', clientId);
        url.searchParams.set('access', accessToken);

        const response = await fetch(url.toString(), { method: 'GET', credentials: 'omit' });
        if (!response.ok) {
          throw new Error(`Failed to load onboarding page (${response.status})`);
        }

        const payload = await response.json();
        if (
          !payload.mediaPackage
          && payload.mediaPackageWorkspaceId
          && ['localhost', '127.0.0.1'].includes(window.location.hostname)
        ) {
          try {
            const mediaSnapshot = await getDoc(doc(db, 'media_packages', payload.mediaPackageWorkspaceId));
            if (mediaSnapshot.exists()) {
              const storedPackage = mediaSnapshot.data() || {};
              const published = storedPackage.published || null;
              if (published?.packageData) {
                payload.mediaPackage = {
                  workspaceId: payload.mediaPackageWorkspaceId,
                  packageData: published.packageData,
                  assets: published.assets || {},
                  version: published.version || 1,
                  publishedAt: published.publishedAt || '',
                  zip: published.zip || null,
                  warnings: published.warnings || [],
                };
              }
            }
          } catch (mediaPackageError) {
            console.warn('Local Media Package preview requires an authenticated builder session.', mediaPackageError);
          }
        }
        if (!cancelled) {
          setClient({ ...payload, id: clientId });
          setError('');
          setIsLoading(false);
        }
      } catch (loadError) {
        console.error('Failed to load public onboarding page:', loadError);
        if (!cancelled) {
          setClient(null);
          setError('This onboarding page is not available.');
          setIsLoading(false);
        }
      }
    };

    loadClient();

    return () => {
      cancelled = true;
    };
  }, [accessToken, clientId]);

  useEffect(() => {
    if (!client) return;

    const savedLanguage = localStorage.getItem(languageStorageKey);
    const clientDefaultLanguage = ONBOARDING_LANGUAGES.some((option) => option.value === client.baseLanguage)
      ? client.baseLanguage
      : DEFAULT_ONBOARDING_LANGUAGE;

    setLanguage(savedLanguage || clientDefaultLanguage);
  }, [client, languageStorageKey]);

  useEffect(() => {
    if (!showPaymentSteps && activeHubSection === 'payments') {
      setActiveHubSection('timeline');
    }
  }, [activeHubSection, showPaymentSteps]);

  const timelineStages = useMemo(() => {
    if (!client) return [];
    return buildTimelineStages(
      translatedStages,
      client.kickoffDate,
      client.stageDurations,
      client.stageStatuses
    );
  }, [client, translatedStages]);

  const timelineSpanDays = useMemo(() => {
    if (timelineStages.length === 0) return 0;
    return diffDays(timelineStages[0].startDate, timelineStages[timelineStages.length - 1].endDate) + 1;
  }, [timelineStages]);

  const timelineWorkingDays = useMemo(() => {
    if (timelineStages.length === 0) return 0;
    return countBusinessDaysInclusive(timelineStages[0].startDate, timelineStages[timelineStages.length - 1].endDate);
  }, [timelineStages]);

  const monthSegments = useMemo(() => {
    if (timelineStages.length === 0 || timelineSpanDays <= 0) return [];

    const timelineStart = timelineStages[0].startDate;
    const timelineEnd = timelineStages[timelineStages.length - 1].endDate;
    const segments = [];
    let cursor = new Date(timelineStart.getFullYear(), timelineStart.getMonth(), 1);

    while (cursor <= timelineEnd) {
      const nextMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      const segmentStart = cursor < timelineStart ? timelineStart : cursor;
      const monthEnd = addDays(nextMonth, -1);
      const segmentEnd = monthEnd > timelineEnd ? timelineEnd : monthEnd;

      segments.push({
        label: new Intl.DateTimeFormat(content.locale, { month: 'short', year: 'numeric' }).format(cursor),
        left: (diffDays(timelineStart, segmentStart) / timelineSpanDays) * 100,
        width: ((diffDays(segmentStart, segmentEnd) + 1) / timelineSpanDays) * 100
      });

      cursor = nextMonth;
    }

    return segments;
  }, [content.locale, timelineSpanDays, timelineStages]);

  const weekendSegments = useMemo(() => {
    if (timelineStages.length === 0 || timelineSpanDays <= 0) return [];

    const timelineStart = toStartOfDay(timelineStages[0].startDate);
    const timelineEnd = toStartOfDay(timelineStages[timelineStages.length - 1].endDate);
    const segments = [];
    let cursor = new Date(timelineStart);

    while (cursor <= timelineEnd) {
      if (!isWeekend(cursor)) {
        cursor = addDays(cursor, 1);
        continue;
      }

      const segmentStart = new Date(cursor);
      while (cursor <= timelineEnd && isWeekend(cursor)) {
        cursor = addDays(cursor, 1);
      }
      const segmentEnd = addDays(cursor, -1);

      segments.push({
        left: (diffDays(timelineStart, segmentStart) / timelineSpanDays) * 100,
        width: ((diffDays(segmentStart, segmentEnd) + 1) / timelineSpanDays) * 100
      });
    }

    return segments;
  }, [timelineSpanDays, timelineStages]);

  const todayLineLeft = useMemo(() => {
    if (timelineStages.length === 0 || timelineSpanDays <= 0) return null;

    const today = new Date();
    const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const timelineStart = timelineStages[0].startDate;
    const timelineEnd = timelineStages[timelineStages.length - 1].endDate;

    if (normalizedToday < timelineStart || normalizedToday > timelineEnd) {
      return null;
    }

    return (clamp(diffDays(timelineStart, normalizedToday), 0, timelineSpanDays - 1) / timelineSpanDays) * 100;
  }, [timelineSpanDays, timelineStages]);

  const timelineMarkers = useMemo(() => {
    if (timelineStages.length === 0 || timelineSpanDays <= 0) return [];
    const stageById = Object.fromEntries(timelineStages.map((stage) => [stage.id, stage]));
    const timelineStart = timelineStages[0].startDate;
    const timelineEnd = timelineStages[timelineStages.length - 1].endDate;
    return [
      { id: 'launch', date: stageById.launch?.startDate, label: 'Communication starts', color: '#e5a3e8' },
      { id: 'draft', date: stageById.draft?.endDate, label: 'Guide v1 online', color: '#9ddcf0', atEnd: true },
      { id: 'translations', date: stageById.translations?.endDate, label: 'Full guide ready', color: '#1687e8', atEnd: true }
    ].map((marker) => {
      if (!marker.date || marker.date < timelineStart || marker.date > timelineEnd) return null;
      const positionDate = marker.atEnd ? addDays(marker.date, 1) : marker.date;
      return { ...marker, left: (clamp(diffDays(timelineStart, positionDate), 0, timelineSpanDays) / timelineSpanDays) * 100 };
    }).filter(Boolean);
  }, [timelineSpanDays, timelineStages]);

  if (isLoading) {
    return <div className="min-h-screen bg-slate-50 p-8 text-sm text-slate-600">{content.copy.loading}</div>;
  }

  if (!client) {
    return <div className="min-h-screen bg-slate-50 p-8 text-sm text-slate-600">{error || content.copy.unavailable}</div>;
  }

  const selectedMaterials = translatedMaterials.filter((item) => client.materials?.[item.id]);
  const promotionPlan = client.promotionPlan || createDefaultPromotionPlan();
  const promotionPlanNotice = getPromotionPlanNotice(client.baseLanguage);
  const isPortuguese = client.baseLanguage === 'pt-PT';
  const launchPromotionCopy = isPortuguese
    ? { title: 'Plano de promoção do lançamento', inProgress: 'Em curso', intro: 'Utilize as mensagens e os materiais preparados para cada atividade. Quando concluir uma tarefa, assinale-a e avance para a seguinte.', before: 'Antes do lançamento', prepare: 'Preparar', launchDay: 'Dia do lançamento', activate: 'Ativar', after: 'Depois do lançamento', repeat: 'Repetir', beforeDescription: 'Crie notoriedade e prepare as equipas antes de o guia ficar público.', launchDescription: 'Torne o guia visível e apresente-o ativamente aos visitantes.', afterDescription: 'Repita a mensagem e relacione o guia com histórias e motivos concretos para visitar.', done: 'Concluído', wording: 'Utilize esta mensagem', channel: 'Canal sugerido', training: 'Abrir formação de lançamento para a equipa' }
    : { title: 'Launch promotion plan', inProgress: 'Work in progress', intro: 'Use the prepared message and materials for each activity. When it is done, check it off and move to the next step.', before: 'Before launch', prepare: 'Prepare', launchDay: 'Launch day', activate: 'Activate', after: 'After launch', repeat: 'Repeat', beforeDescription: 'Build awareness and prepare the teams before the guide is public.', launchDescription: 'Make the guide visible and actively introduce it to visitors.', afterDescription: 'Repeat the message and connect the guide to real stories and reasons to visit.', done: 'Done', wording: 'Use this wording', channel: 'Suggested channel', training: 'Open Client launch training' };
  const promotionTasks = createDefaultPromotionPlan().tasks.map((defaultTask) => ({
    ...defaultTask,
    ...(promotionPlan.tasks || []).find((task) => task.id === defaultTask.id)
  }));
  const receivedMaterials = selectedMaterials.filter((item) => client.materialsReceived?.[item.id]).length;
  const completedReviewItems = translatedReviewItems.filter((item) => client.reviewChecklist?.[item.id]).length;
  const selectedQrPlacements = translatedQrPlacements.filter((item) => client.qrPlacements?.[item.id]);
  const activeLaunchAssets = translatedLaunchAssets.filter((item) => Boolean(client.launchAssets?.[item.id]?.trim()));
  const guideLanguageRows = GUIDE_LANGUAGE_OPTIONS.map((option) => ({
    ...option,
    label: content.guideLanguages?.[option.value] || option.label,
    ...getGuideLanguageState(client, option.value)
  }));
  const activeMediaPressItems = translatedMediaPressItems.map((item) => {
    const value = client.mediaPress?.[item.id] || '';
    const links = item.id === 'pressCoverageLinks'
      ? value.split(/\r?\n/).map(parseLabeledLink).filter(Boolean)
      : value.trim() ? [parseLabeledLink(value)].filter(Boolean) : [];

    return {
      ...item,
      value,
      links
    };
  }).filter((item) => item.links.length > 0);
  const selectedProjectType = translatedProjectTypes.find((type) => type.value === client.projectType)?.label || content.copy.projectTypeNotSet;
  const activeStage = timelineStages.find((stage) => stage.id === client.activeStageId) || timelineStages[0];
  const visiblePaymentItems = translatedPaymentItems.filter((item) => (
    (item.type === 'contract' && showContractSteps) || (item.type === 'payment' && showPaymentSteps)
  ));
  const estimatedLaunchDate = timelineStages.length > 0
    ? formatDate(timelineStages.find((stage) => stage.id === 'translations')?.endDate || timelineStages[timelineStages.length - 1].endDate, content.locale)
    : content.copy.noStages;
  const agreedLaunchDate = client.agreedLaunchDate
    ? formatDate(new Date(`${client.agreedLaunchDate}T00:00:00`), content.locale)
    : content.copy.notSetYet;
  const isReviewApproved = Boolean(client.reviewChecklist?.clientGuideText);

  const handleReviewTextChange = async (checked) => {
    if (!client || isMarkingReviewText) return;

    setIsMarkingReviewText(true);
    setReviewTextStatus('');

    try {
      const response = await fetch(ONBOARDING_MARK_REVIEW_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'markClientReviewTextReviewed',
          clientId,
          access: accessToken,
          reviewed: checked
        })
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to update review status.');
      }

      setClient((current) => (current ? ({
        ...current,
        reviewChecklist: {
          ...(current.reviewChecklist || {}),
          clientGuideText: checked
        },
        clientReviewAcknowledgedAt: checked ? new Date().toISOString() : null
      }) : current));

      setReviewTextStatus(checked
        ? 'Thanks. We have received the review note.'
        : 'Review note cleared.');
    } catch (updateError) {
      console.error('Failed to update review status:', updateError);
      setReviewTextStatus(updateError.message || 'Failed to update review status.');
    } finally {
      setIsMarkingReviewText(false);
    }
  };

  const handleReviewTextClick = (event) => {
    if (isReviewApproved || isMarkingReviewText) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setShowReviewConfirm(true);
  };

  const handleConfirmReviewText = async () => {
    setShowReviewConfirm(false);
    await handleReviewTextChange(true);
  };

  const handlePromotionTaskUpdate = async (taskId, completed) => {
    if (!client || promotionUpdateTaskId) return;

    setPromotionUpdateTaskId(taskId);
    setPromotionUpdateStatus('');
    try {
      const response = await fetch(ONBOARDING_MARK_REVIEW_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'updateClientPromotionTask',
          clientId,
          access: accessToken,
          taskId,
          completed
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(response.status === 405 || response.status === 404
          ? 'The checklist service is not updated in this environment yet. Nothing was saved.'
          : (payload.error || 'Failed to update promotion task.'));
      }
      setClient((current) => current ? { ...current, promotionPlan: { ...(current.promotionPlan || {}), tasks: payload.tasks } } : current);
      setPromotionUpdateStatus('Promotion plan updated.');
    } catch (updateError) {
      setPromotionUpdateStatus(updateError.message || 'Failed to update promotion task.');
    } finally {
      setPromotionUpdateTaskId('');
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: `radial-gradient(circle at top, ${BRAND_CYAN}18 0%, transparent 26%), linear-gradient(180deg, #eff8fb 0%, #f8fbfc 22%, #eef4f6 100%)` }}
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8">
        <section
          className="relative overflow-hidden rounded-[24px] border border-white/15 p-4 text-white shadow-[0_20px_60px_rgba(1,43,57,0.24)] sm:p-5 print:hidden"
          style={{ background: `radial-gradient(circle at top left, ${BRAND_CYAN}30 0%, transparent 24%), linear-gradient(135deg, ${BRAND_DEEP} 0%, ${BRAND_MID} 58%, #000d1a 100%)` }}
        >
          <div className="absolute right-[-4rem] top-[-4rem] h-40 w-40 rounded-full bg-cyan-300/10 blur-3xl" />
          <div className="absolute bottom-[-5rem] left-[-2rem] h-44 w-44 rounded-full bg-yellow-200/10 blur-3xl" />
          <div className="relative flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100/80">{content.copy.onboardingLabel}</div>
              <div className="mt-2 text-lg font-semibold tracking-tight text-white sm:text-xl">{content.copy.hubTitle}</div>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight text-white sm:text-3xl">{client.name}</h1>
              {client.guideName && (
                <div className="mt-1 inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-cyan-50">{client.guideName}</div>
              )}
              <p className="mt-2 max-w-3xl text-xs leading-5 text-slate-200">
                {selectedProjectType} | {activeStage?.title || content.copy.noActivePhase} | {selectedMaterials.length} {content.copy.pageSummary} | {receivedMaterials}/{selectedMaterials.length || 0} {content.copy.received}
              </p>
            </div>
            <label className="grid gap-1 text-sm lg:w-48">
              <span className="text-xs font-medium uppercase tracking-[0.18em] text-cyan-100/80">{content.copy.languageLabel}</span>
              <select
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
                className="rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-xs text-white outline-none backdrop-blur focus:border-cyan-300"
              >
                {ONBOARDING_LANGUAGES.map((option) => (
                  <option key={option.value} value={option.value} className="text-slate-900">{option.label}</option>
                ))}
              </select>
            </label>
          </div>
        </section>

        {!hasEnteredHub ? (
          <>
            <section className={PANEL_CLASS}>
              <div className="text-sm font-semibold text-slate-900">{content.copy.hubTitle}</div>
              <p className="mt-2 max-w-3xl text-base leading-7 text-slate-800">{content.copy.hubIntro}</p>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                {content.copy.hubDescription}
              </p>
              <div className="mx-auto mt-4 grid w-full max-w-2xl gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-violet-100 bg-violet-50/70 p-4">
                  <div className="text-xs font-semibold uppercase text-slate-500">{content.copy.guideQrTitle}</div>
                  {client.guideQrImageUrl ? (
                    <div className="mt-2 grid gap-2">
                      <img
                        src={client.guideQrImageUrl}
                        alt="Guide QR code"
                        className="h-48 w-48 rounded-md border border-violet-100 bg-white p-2 object-contain"
                      />
                    </div>
                  ) : (
                    <div className="mt-1 text-lg font-semibold text-slate-900">{content.copy.guideQrMissing}</div>
                  )}
                </div>
                <div className="rounded-2xl border border-amber-100 bg-amber-50/80 p-4">
                  <div className="text-xs font-semibold uppercase text-slate-500">{content.copy.estimatedLaunchDate} + {content.copy.agreedLaunchDate}</div>
                  <div className="mt-2 grid gap-2">
                    <div>
                      <div className="text-[11px] font-semibold uppercase text-slate-500">{content.copy.estimatedLaunchDate}</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">{estimatedLaunchDate}</div>
                    </div>
                    <div className="border-t border-amber-100 pt-2">
                      <div className="text-[11px] font-semibold uppercase text-slate-500">{content.copy.agreedLaunchDate}</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">{agreedLaunchDate}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setHasEnteredHub(true)}
                  className="inline-flex min-w-[280px] items-center justify-center rounded-full bg-slate-950 px-8 py-4 text-base font-semibold text-white shadow-[0_18px_36px_rgba(1,43,57,0.28)] transition hover:-translate-y-0.5 hover:bg-[#003E47] sm:min-w-[340px] sm:px-10"
                >
                  {content.copy.openHubButton}
                </button>
              </div>
            </section>

            <section className="grid gap-5 lg:grid-cols-2">
              <div className={`${PANEL_CLASS} lg:col-span-1`}>
                <div className="text-sm font-semibold text-slate-900">{content.copy.welcomeTitle}</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">{content.copy.welcomeBody}</p>
                <p className="mt-2 text-sm leading-6 text-slate-600">{content.copy.welcomeSupporting}</p>
              </div>
              <div className={PANEL_CLASS}>
                <div className="text-sm font-semibold text-slate-900">{content.copy.buildTitle}</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">{content.copy.buildBody}</p>
                <div className="mt-3 text-xs font-semibold uppercase text-slate-500">{content.copy.buildListTitle}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {content.copy.buildList.map((item) => (
                    <span key={item} className="rounded-full border border-cyan-100 bg-cyan-50 px-3 py-1 text-xs font-medium text-cyan-900">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div className={PANEL_CLASS}>
                <div className="text-sm font-semibold text-slate-900">{content.copy.accessibilityTitle}</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">{content.copy.accessibilityBody}</p>
                <div className="mt-3 grid gap-2">
                  {content.copy.accessibilityPoints.map((item) => (
                    <div key={item} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={PANEL_CLASS}>
                <div className="text-sm font-semibold text-slate-900">{content.copy.analyticsTitle}</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">{content.copy.analyticsBody}</p>
                <div className="mt-4">
                  <button
                    type="button"
                    className={BUTTON_PRIMARY_CLASS}
                  >
                    {content.copy.analyticsButton}
                  </button>
                </div>
              </div>
            </section>
          </>
        ) : (
          <>
            <section className={PANEL_CLASS}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                <span><span className="font-medium text-slate-700">{content.copy.estimatedLaunchDate}:</span> {estimatedLaunchDate}</span>
                <span className="hidden text-slate-300 sm:inline">|</span>
                <span><span className="font-medium text-slate-700">{content.copy.agreedLaunchDate}:</span> {agreedLaunchDate}</span>
              </div>

              <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-200/80 pt-4">
                {visibleHubSections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setActiveHubSection(section.id === 'project-details' ? 'documents' : section.id === 'production' ? 'materials' : section.id === 'launch' ? 'qr-codes' : section.id)}
                    className={`rounded-full px-4 py-2.5 text-sm font-medium transition ${
                    activeHubSection === section.id || (section.id === 'launch' && LAUNCH_SUBSECTIONS.some((item) => item.id === activeHubSection)) || (section.id === 'project-details' && PROJECT_SUBSECTIONS.some((item) => item.id === activeHubSection)) || (section.id === 'production' && PRODUCTION_SUBSECTIONS.some((item) => item.id === activeHubSection))
                        ? 'bg-slate-950 text-white shadow-[0_10px_24px_rgba(1,43,57,0.22)]'
                        : 'border border-slate-200 bg-white/80 text-slate-700 hover:border-cyan-200 hover:bg-cyan-50'
                    }`}
                  >
                    {section.label}
              </button>
            ))}
          </div>
          {LAUNCH_SUBSECTIONS.some((item) => item.id === activeHubSection) && (
            <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-200/20 pt-3">
              {LAUNCH_SUBSECTIONS.map((section) => (
                <button key={section.id} type="button" onClick={() => setActiveHubSection(section.id)} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${activeHubSection === section.id ? 'bg-cyan-700 text-white' : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>
                  {section.icon && React.createElement(section.icon, { className: 'h-3.5 w-3.5' })}
                  {content.tabs[section.id === 'launch' ? 'launch-plan' : section.id] || section.label}
                </button>
              ))}
            </div>
          )}
          {PROJECT_SUBSECTIONS.some((item) => item.id === activeHubSection) && (
            <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-200/80 pt-3">
              {PROJECT_SUBSECTIONS.map((section) => <button key={section.id} type="button" onClick={() => setActiveHubSection(section.id)} className={`rounded-md px-3 py-1.5 text-xs font-medium ${activeHubSection === section.id ? 'bg-slate-700 text-white' : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>{content.tabs[section.id] || section.label}</button>)}
            </div>
          )}
          {PRODUCTION_SUBSECTIONS.some((item) => item.id === activeHubSection) && (
            <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-200/80 pt-3">
              {PRODUCTION_SUBSECTIONS.map((section) => <button key={section.id} type="button" onClick={() => setActiveHubSection(section.id)} className={`rounded-md px-3 py-1.5 text-xs font-medium ${activeHubSection === section.id ? 'bg-slate-700 text-white' : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>{content.tabs[section.id] || section.label}</button>)}
            </div>
          )}
        </section>

        {activeHubSection === 'timeline' && (
        <section className={PANEL_CLASS}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <CalendarDays className="h-4 w-4 text-slate-500" />
                {content.copy.projectTimeline}
              </div>
              <div className="mt-1 text-sm text-slate-500">
                {timelineStages.length > 0 ? `${formatDate(timelineStages[0].startDate, content.locale)} ${content.copy.to} ${formatDate(timelineStages[timelineStages.length - 1].endDate, content.locale)} | ${timelineWorkingDays} ${content.copy.workingDays}` : content.copy.noStages}
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: GUIDE_SOFIA_BAR_COLOR }} />
                {content.copy.guideSofiaLegend}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: CLIENT_BAR_COLOR }} />
                {content.copy.clientLegend}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: BOTH_BAR_COLOR }} />
                {content.copy.sharedLegend}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm bg-slate-200" />
                {content.copy.weekendLegend}
              </span>
            </div>
          </div>

          <div className="mt-5 overflow-x-auto">
            <div className="min-w-[900px]">
              <div className="relative h-8 rounded-t-md bg-slate-50">
                {weekendSegments.map((segment, index) => (
                  <span
                    key={`header-weekend-${index}`}
                    className="absolute bottom-0 top-0 bg-slate-200/80"
                    style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
                  />
                ))}
                {monthSegments.map((month) => (
                  <div
                    key={month.label}
                    className="absolute top-0 h-full border-l border-slate-200 px-2 py-2 text-xs font-medium text-slate-500"
                    style={{ left: `${month.left}%`, width: `${month.width}%` }}
                  >
                    {month.label}
                  </div>
                ))}
                {todayLineLeft !== null && (
                  <>
                    <span
                      className="absolute bottom-0 top-0 z-0 w-1 -translate-x-1/2 rounded-full bg-yellow-400 shadow-[0_0_0_1px_rgba(255,255,255,0.9)]"
                      style={{ left: `${todayLineLeft}%` }}
                    />
                    <span
                      className="absolute top-1 z-30 -translate-x-1/2 rounded-full bg-slate-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm"
                      style={{ left: `${todayLineLeft}%` }}
                    >
                      {content.copy.today}
                    </span>
                  </>
                )}
                {timelineMarkers.map((marker) => (
                  <React.Fragment key={`marker-header-${marker.id}`}>
                    <span className="absolute bottom-0 top-0 z-40 w-0.5 -translate-x-1/2" style={{ left: `${marker.left}%`, backgroundColor: marker.color }} />
                    <span className="absolute top-1 z-50 -translate-x-1/2 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm" style={{ left: `${marker.left}%`, backgroundColor: marker.color }}>
                      {marker.label} · {formatDate(marker.date, content.locale)}
                    </span>
                  </React.Fragment>
                ))}
              </div>

              <div className="divide-y divide-slate-100">
                {timelineStages.map((stage) => {
                  const left = (diffDays(timelineStages[0].startDate, stage.startDate) / timelineSpanDays) * 100;
                  const width = ((diffDays(stage.startDate, stage.endDate) + 1) / timelineSpanDays) * 100;

                  return (
                    <div key={stage.id} className="pt-3 pb-0">
                      <div className="relative h-12 w-full rounded-2xl border border-slate-200/80 bg-slate-100/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]">
                        {weekendSegments.map((segment, index) => (
                          <span
                            key={`${stage.id}-weekend-${index}`}
                            className="absolute bottom-0 top-0 bg-slate-200/80"
                            style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
                          />
                        ))}
                        <span className="absolute left-3 top-1 z-10 flex max-w-[38%] items-center gap-1">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-white text-[10px] font-semibold text-slate-700">
                            {stage.step}
                          </span>
                          <span className="truncate text-[10px] font-medium leading-none text-slate-900">
                            {stage.title}
                          </span>
                        </span>
                        <span className="absolute right-3 top-1/2 z-10 flex max-w-[calc(100%-1.5rem)] items-center justify-end gap-2 -translate-y-1/3 text-xs text-slate-600">
                          <span className="truncate">{stage.owner}</span>
                          <span className="text-slate-400">|</span>
                          <span className="truncate">{formatDate(stage.startDate, content.locale)} {content.copy.to} {formatDate(stage.endDate, content.locale)}</span>
                          <span className="text-slate-400">|</span>
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CHIP_CLASSES[stage.status] || STATUS_CHIP_CLASSES.todo}`}>
                            {content.statuses[stage.status] || stage.status}
                          </span>
                          <span className="text-slate-400">|</span>
                          <span className="shrink-0">{stage.durationDays}d</span>
                        </span>
                        {monthSegments.map((month) => (
                          <span
                            key={`${stage.id}-${month.label}`}
                            className="absolute top-0 h-full border-l border-slate-200"
                            style={{ left: `${month.left}%` }}
                          />
                        ))}
                        {todayLineLeft !== null && (
                          <span
                            className="absolute bottom-0 top-0 z-0 w-1 -translate-x-1/2 rounded-full bg-yellow-400 shadow-[0_0_0_1px_rgba(255,255,255,0.9)]"
                            style={{ left: `${todayLineLeft}%` }}
                          />
                        )}
                        {timelineMarkers.map((marker) => (
                          <span key={`${stage.id}-${marker.id}`} className="absolute bottom-0 top-0 z-40 w-0.5 -translate-x-1/2" style={{ left: `${marker.left}%`, backgroundColor: marker.color }} />
                        ))}
                        <span
                          className="absolute top-5 z-40 h-6 rounded-md px-3 text-[10px] font-semibold leading-6 text-white shadow-sm"
                          style={{
                            left: `${left}%`,
                            width: `${Math.max(width, stage.durationDays === 0 ? 1.2 : width)}%`,
                            minWidth: '44px',
                            backgroundColor: stage.color
                          }}
                        >
                          {stage.durationDays}d
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'materials' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className={PANEL_CLASS}>
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-slate-900">{content.copy.whatWeNeed}</div>
              <div className="text-sm font-medium text-slate-600">{receivedMaterials}/{selectedMaterials.length || 0} {content.copy.received}</div>
            </div>
            <div className="mt-4 grid gap-2">
              {selectedMaterials.map((item) => {
                const Icon = item.icon;
                const received = Boolean(client.materialsReceived?.[item.id]);
                return (
                  <div key={item.id} className="flex items-center gap-3 rounded-2xl border border-slate-200 px-3 py-3 text-sm">
                    {received ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-400" />}
                    <Icon className="h-4 w-4 text-slate-500" />
                    <span className="text-slate-800">{item.label}</span>
                    <span className={`ml-auto rounded-full px-2 py-0.5 text-xs font-medium ${received ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                      {received ? content.copy.received : content.copy.pending}
                    </span>
                  </div>
                );
              })}
              {selectedMaterials.length === 0 && (
                <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
                  {content.copy.noMaterialRequests}
                </div>
              )}
            </div>
            {client.driveUploadUrl && (
              <a
                href={client.driveUploadUrl}
                target="_blank"
                rel="noreferrer"
                className={`${BUTTON_PRIMARY_CLASS} mt-4 gap-2`}
              >
                <Upload className="h-4 w-4" />
                {content.copy.uploadDrive}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>

        </section>
        )}

        {activeHubSection === 'languages' && (
        <section className="grid gap-5">
          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">{content.copy.languagesTitle}</div>
            <p className="mt-1 text-sm text-slate-500">{content.copy.languagesHelp}</p>
            <div className="mt-4 grid gap-2">
              {guideLanguageRows.map((languageRow) => {
                const selected = Boolean(languageRow.selected);
                const ready = Boolean(languageRow.ready);
                const statusLabel = selected ? content.copy.selected : content.copy.notSelected;

                return (
                  <div
                    key={languageRow.value}
                    className={`flex items-center gap-3 rounded-2xl border px-3 py-3 text-sm transition ${
                      selected
                        ? 'border-slate-200 bg-white text-slate-900'
                      : 'border-slate-200 bg-slate-50 text-slate-400 opacity-70'
                    }`}
                  >
                    {ready ? <CheckCircle2 className={`h-4 w-4 ${selected ? 'text-emerald-600' : 'text-slate-300'}`} /> : <Circle className="h-4 w-4 text-slate-300" />}
                    <div className="min-w-0 flex-1">
                      <div className={`font-medium ${selected ? 'text-slate-900' : 'text-slate-400'}`}>{languageRow.label}</div>
                      <div className="text-xs text-slate-500">
                        {languageRow.value === client.baseLanguage ? content.copy.baseLanguage : content.copy.additionalLanguage}
                      </div>
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${selected ? 'bg-cyan-100 text-cyan-800' : 'bg-slate-100 text-slate-500'}`}>
                      {statusLabel}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ready ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                      {ready ? content.copy.ready : content.copy.inProgress}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'review' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">{content.copy.guidesofiaQa}</div>
            <div className="mt-1 text-sm text-slate-500">{content.copy.guidesofiaQaDescription}</div>
            <div className="mt-4 grid gap-2">
              {translatedReviewItems.filter((item) => item.owner === 'guidesofia').map((item) => {
                const checked = Boolean(client.reviewChecklist?.[item.id]);
                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 rounded-md border px-3 py-3 text-sm ${
                      checked ? 'border-emerald-500 bg-emerald-200' : 'border-slate-200 bg-slate-50'
                    }`}
                  >
                    {checked ? <CheckCircle2 className="h-4 w-4 text-emerald-700" /> : <Circle className="h-4 w-4 text-slate-400" />}
                    <span className="text-slate-800">{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={`${PANEL_CLASS} lg:col-span-1`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">{content.copy.reviewDocument}</div>
                <div className="mt-1 text-sm text-slate-500">{content.copy.reviewDocumentHelp}</div>
              </div>
              <div className="text-sm font-medium text-slate-600">{completedReviewItems}/{translatedReviewItems.length} {content.copy.complete}</div>
            </div>
            <div className="mt-4">
              {client.reviewDocUrl ? (
                <a
                  href={client.reviewDocUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={`${BUTTON_PRIMARY_CLASS} w-full`}
                >
                  {content.copy.openReviewDoc}
                </a>
              ) : (
                <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
                  {content.copy.reviewDocMissing}
                </div>
              )}
            </div>
            <label
              className={`mt-4 flex cursor-pointer items-start gap-3 rounded-md border px-3 py-3 text-sm ${
                isReviewApproved ? 'border-emerald-600 bg-emerald-400' : 'border-yellow-400 bg-yellow-300 hover:bg-yellow-400'
              }`}
            >
              <input
                type="checkbox"
                checked={isReviewApproved}
                disabled={isMarkingReviewText || isReviewApproved}
                onClick={handleReviewTextClick}
                onChange={() => {}}
                className={`mt-1 h-4 w-4 rounded border-slate-300 ${isReviewApproved ? 'accent-emerald-700' : 'accent-yellow-500'}`}
              />
              <div>
                <div className={`font-medium ${isReviewApproved ? 'text-white' : 'text-slate-950'}`}>{content.copy.reviewTextReviewedLabel}</div>
                <div className={`mt-1 text-xs leading-5 ${isReviewApproved ? 'text-white/90' : 'text-slate-900'}`}>
                  {isReviewApproved ? content.copy.reviewTextReviewedApprovedStatus : content.copy.reviewTextReviewedPendingStatus}
                </div>
              </div>
            </label>
            {showReviewConfirm && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-sm"
                onClick={() => setShowReviewConfirm(false)}
              >
                <div
                  className="w-full max-w-lg rounded-[28px] border border-yellow-300 bg-white p-6 shadow-[0_30px_100px_rgba(0,0,0,0.35)]"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-yellow-700">{content.copy.reviewTextReviewedConfirmTitle}</div>
                  <div className="mt-3 text-lg font-semibold text-slate-900">{content.copy.reviewTextReviewedConfirmBody}</div>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => setShowReviewConfirm(false)}
                      className="rounded-full bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700"
                    >
                      {content.copy.reviewTextReviewedCancelAction}
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmReviewText}
                      className="rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
                    >
                      {content.copy.reviewTextReviewedConfirmAction}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {reviewTextStatus && (
              <div className="mt-2 text-xs text-slate-500">{reviewTextStatus}</div>
            )}
            <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Other client review points</div>
            <div className="mt-2 grid gap-2">
              {translatedReviewItems.filter((item) => item.owner === 'client' && item.id !== 'clientGuideText').map((item) => {
                const checked = Boolean(client.reviewChecklist?.[item.id]);
                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 rounded-md border px-3 py-3 text-sm ${
                      checked ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200'
                    }`}
                  >
                    {checked ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-400" />}
                    <span className="text-slate-800">{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'qr-codes' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className={`${PANEL_CLASS} lg:col-span-2`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-lg font-semibold text-slate-900">{isPortuguese ? 'Ficheiros QR Code' : 'QR code files'}</div>
                <div className="mt-1 text-sm text-slate-500">{isPortuguese ? 'Acede à pasta partilhada com todos os QR codes e ficheiros finais preparados para implementação.' : 'Open the shared folder containing all QR codes and final files prepared for implementation.'}</div>
              </div>
              <ScanLine className="h-6 w-6 text-cyan-700" />
            </div>
            <div className="mt-5">
              {client.qrFilesUrl ? (
                <a href={client.qrFilesUrl} target="_blank" rel="noreferrer" className={`${BUTTON_PRIMARY_CLASS} gap-2`}>
                  {isPortuguese ? 'Download todos os QR codes' : 'Download all QR codes'}
                  <ExternalLink className="h-4 w-4" />
                </a>
              ) : (
                <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-sm text-slate-500">
                  {isPortuguese ? 'A pasta com os ficheiros QR ainda não foi adicionada.' : 'The shared QR files folder has not been added yet.'}
                </div>
              )}
            </div>
          </div>

          <div className={PANEL_CLASS}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">{content.copy.guideQrTitle}</div>
                <div className="mt-1 text-sm text-slate-500">{content.copy.guideQrHelp}</div>
              </div>
              <ScanLine className="h-5 w-5 text-slate-500" />
            </div>
            <div className="mt-4 grid gap-3">
              {client.guideQrImageUrl ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <img src={client.guideQrImageUrl} alt="Guide QR code" className="mx-auto max-h-72 rounded-md border border-slate-200 bg-white p-3 shadow-sm" />
                  <div className="mt-3 flex flex-wrap gap-2">
                    <a href={client.guideQrImageUrl} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center rounded-full bg-slate-950 px-4 py-2 text-sm font-medium text-white transition hover:bg-[#003E47]">{content.copy.openGuideQr}</a>
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">{content.copy.guideQrMissing}</div>
              )}
            </div>
          </div>

          <div className={PANEL_CLASS}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">{content.copy.qrImplementation}</div>
                <div className="mt-1 text-sm text-slate-500">{content.copy.qrImplementationHelp}</div>
              </div>
              <ScanLine className="h-5 w-5 text-slate-500" />
            </div>
            <div className="mt-4 grid gap-2">
              {selectedQrPlacements.map((item) => (
                <div key={item.id} className="flex items-center gap-3 rounded-2xl border border-slate-200 px-3 py-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="text-slate-800">{item.label}</span>
                </div>
              ))}
              {selectedQrPlacements.length === 0 && <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">{content.copy.noQrPlacements}</div>}
            </div>
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-600">{content.copy.qrCallToAction}</div>
          </div>
        </section>
        )}

        {activeHubSection === 'launch' && (
        <section>
          <div className={PANEL_CLASS}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">{content.copy.launchChecklist}</div>
                <div className="mt-1 text-sm text-slate-500">{content.copy.launchChecklistHelp}</div>
              </div>
              <Megaphone className="h-5 w-5 text-slate-500" />
            </div>
            <div className="mt-4 grid gap-2">
              {translatedLaunchItems.map((item) => {
                const checked = Boolean(client.launchChecklist?.[item.id]);
                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 rounded-md border px-3 py-3 text-sm ${
                      checked ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200'
                    }`}
                  >
                    {checked ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-400" />}
                    <span className="text-slate-800">{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'add-ons' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className={PANEL_CLASS}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">{content.copy.addOnsTitle}</div>
                <div className="mt-1 text-sm text-slate-500">{content.copy.addOnsHelp}</div>
              </div>
              <Sparkles className="h-5 w-5 text-slate-500" />
            </div>
            <div className="mt-4 grid gap-3">
              {translatedAddOns.map((item) => {
                const Icon = item.icon;
                const checked = Boolean(client.addOns?.[item.id]);

                return (
                  <div
                    key={item.id}
                    className={`rounded-md border p-4 ${
                      checked ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {checked ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" /> : <Circle className="mt-0.5 h-4 w-4 text-slate-400" />}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${checked ? 'text-emerald-600' : 'text-slate-500'}`} />
                          <div className={`font-medium ${checked ? 'text-slate-900' : 'text-slate-500'}`}>{item.label}</div>
                        </div>
                        <div className={`mt-2 text-sm leading-6 ${checked ? 'text-slate-700' : 'text-slate-500'}`}>
                          {item.description}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">{content.copy.addOnsNextTitle}</div>
            <div className="mt-2 text-sm leading-6 text-slate-600">{content.copy.addOnsNextHelp}</div>
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700">
              {content.copy.addOnsNextBody}
            </div>
            {client.meetingLink ? (
              <div className="mt-4">
                <a
                  href={client.meetingLink}
                  target="_blank"
                  rel="noreferrer"
                  className={`${BUTTON_PRIMARY_CLASS} w-full`}
                >
                  {content.copy.openBookingLink}
                </a>
              </div>
            ) : null}
          </div>
        </section>
        )}

        {activeHubSection === 'launch-assets' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">{content.copy.launchAssetsTitle}</div>
            <p className="mt-1 text-sm text-slate-500">{content.copy.launchAssetsHelp}</p>
            <div className="mt-4 grid gap-3">
              {translatedLaunchAssets.map((item) => {
                const value = client.launchAssets?.[item.id] || '';
                const links = value.split(/\r?\n/).map(parseLabeledLink).filter(Boolean);

                return (
                  <div key={item.id} className="rounded-md border border-slate-200 p-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">{item.label}</div>
                    <div className="mt-2 grid gap-2">
                      {links.length > 0 ? links.map((link, index) => (
                        <a
                          key={`${item.id}-${index}`}
                          href={link.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex w-full items-center justify-between gap-3 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-medium text-white shadow-[0_10px_24px_rgba(1,43,57,0.24)] transition hover:-translate-y-0.5 hover:bg-[#003E47]"
                        >
                          <span className="truncate">{link.label}</span>
                          <ExternalLink className="h-4 w-4 shrink-0" />
                        </a>
                      )) : (
                        <div className="text-sm text-slate-500">{content.copy.noLinksAdded}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">{content.copy.launchAssetsSummary}</div>
            <div className="mt-4 grid gap-2">
              {activeLaunchAssets.length > 0 ? (
                activeLaunchAssets.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 rounded-2xl border border-slate-200 px-3 py-3 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    <span className="text-slate-800">{item.label}</span>
                  </div>
                ))
              ) : (
                <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
                  {content.copy.launchAssetsEmpty}
                </div>
              )}
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'media-press' && (
        <ClientMediaPackageView mediaPackage={client.mediaPackage} language={language} />
        )}

        {activeHubSection === 'documents' && (
        <section className={PANEL_CLASS}>
          <div className="text-sm font-semibold text-slate-900">{content.copy.documentsTitle}</div>
          <p className="mt-1 text-sm text-slate-500">{content.copy.documentsHelp}</p>
          <div className="mt-4">
            {client.documentsUrl ? (
              <a
                href={client.documentsUrl}
                target="_blank"
                rel="noreferrer"
                className={`${BUTTON_PRIMARY_CLASS} gap-2`}
              >
                <FileText className="h-4 w-4" />
                {content.copy.openDocumentsLink}
              </a>
            ) : (
              <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
                {content.copy.documentsMissing}
              </div>
            )}
          </div>
        </section>
        )}

        {activeHubSection === 'scope' && (
        <section className="grid gap-5 lg:grid-cols-2">
          {(() => { const profile = SCOPE_PRODUCT_PROFILES[client.scope?.product || 'essential']; return <div className={`${PANEL_CLASS} lg:col-span-2`}><div className="text-xs font-semibold uppercase tracking-wide text-cyan-800">Product scope</div><div className="mt-1 text-xl font-semibold text-slate-900">{profile.label}</div><p className="mt-1 text-sm text-slate-600">{profile.idealFor}</p><div className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4"><div><div className="text-xs font-semibold uppercase text-slate-500">Guide scope</div><div className="mt-1 text-slate-800">{profile.pois} / {profile.words}</div></div><div><div className="text-xs font-semibold uppercase text-slate-500">Content source</div><div className="mt-1 text-slate-800">{profile.source}</div></div><div><div className="text-xs font-semibold uppercase text-slate-500">Revisions</div><div className="mt-1 text-slate-800">{profile.revisions}</div></div><div><div className="text-xs font-semibold uppercase text-slate-500">Languages</div><div className="mt-1 text-slate-800">{profile.languages}</div></div></div><div className="mt-4 border-t border-slate-200 pt-4"><div className="text-xs font-semibold uppercase text-slate-500">Included content refreshes</div><div className="mt-1 text-sm text-slate-800">{profile.refreshes}</div><div className="mt-4 text-xs font-semibold uppercase text-slate-500">Included platform features</div><div className="mt-2 grid gap-2 sm:grid-cols-2">{profile.features.map((feature) => <div key={feature} className="flex items-center gap-2 text-sm text-slate-800"><span className="text-emerald-600">✓</span>{feature}</div>)}</div></div></div>; })()}
          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">Agreed scope</div>
            <div className="mt-4 grid gap-4">
              {client.scope?.stops && <div><div className="text-xs font-semibold uppercase text-slate-500">Confirmed stops / POIs</div><p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-700">{client.scope.stops}</p></div>}
              <div><div className="text-xs font-semibold uppercase text-slate-500">Included languages</div><p className="mt-1 text-sm leading-6 text-slate-700">{Object.entries(client.guideLanguages || {}).filter(([, value]) => value?.selected).map(([language]) => language).join(', ') || content.copy.notSetYet}</p></div>
              {[['excluded', 'Specific exclusions or agreements'], ['changeRequestPolicy', 'Requests after approval']].map(([key, label]) => (
                <div key={key}>
                  <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-700">{client.scope?.[key] || content.copy.notSetYet}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">Scope approval</div>
            <p className="mt-2 text-sm leading-6 text-slate-600">{client.scope?.approvedAt ? `Approved by ${client.scope.approvedBy || 'the client'} on ${formatDate(new Date(`${client.scope.approvedAt}T00:00:00`), content.locale)}.` : 'Scope approval is still pending.'}</p>
            <div className={`mt-4 rounded-md border px-3 py-3 text-sm ${client.scope?.approvedAt ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
              {client.scope?.approvedAt ? 'The agreed boundary is recorded for this project.' : 'Please confirm the scope with your GuideSofia project manager before production begins.'}
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'launch-training' && (
        <section className="grid items-start gap-5 lg:grid-cols-2">
          <div className={`${PANEL_CLASS} lg:col-span-2`}>
            <div className="text-sm font-semibold text-slate-900">{isPortuguese ? 'Formação para o lançamento' : 'Client launch training'}</div>
            <p className="mt-1 text-sm leading-6 text-slate-600">{isPortuguese ? 'Utilize este guia para tornar a experiência visível, ajudar a equipa a recomendá-la e preparar um lançamento fluido para os visitantes.' : 'Use this guide to make the experience visible, help staff recommend it and prepare for a smooth visitor launch.'}</p>
            {client.baseLanguage === 'pt-PT' && <StaffTrainingGuide steps={getStaffTrainingSteps(client.baseLanguage)} institutionName={client.name} guideName={client.guideName} qrImageUrl={client.guideQrImageUrl} />}
          </div>
          {getLaunchTrainingSteps(client.baseLanguage).map(([title, body], index) => {
            const hasExpansionState = Object.prototype.hasOwnProperty.call(expandedTrainingItems, title);
            const isOpen = hasExpansionState ? Boolean(expandedTrainingItems[title]) : index === 0;
            return (
              <div className={`${PANEL_CLASS} overflow-hidden`}>
                <button type="button" onClick={() => setExpandedTrainingItems((current) => ({ ...current, [title]: !isOpen }))} className="flex w-full items-center gap-3 text-left">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-xs font-semibold text-cyan-900">{index + 1}</span>
                  <span className="flex-1 text-sm font-semibold text-slate-900">{title}</span>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && <div className="mt-4 grid gap-2 border-t border-slate-200 pt-3">{body.split(' | ').filter((part) => !/^(DONE|CONCLUÍDO):/.test(part)).map((part) => { const [label, ...text] = part.split(': '); const meta = { WHY: { icon: Lightbulb, tone: 'border-amber-200 bg-amber-50 text-amber-950' }, 'PORQUÊ': { icon: Lightbulb, tone: 'border-amber-200 bg-amber-50 text-amber-950' }, DO: { icon: ListChecks, tone: 'border-cyan-200 bg-cyan-50 text-cyan-950' }, FAZER: { icon: ListChecks, tone: 'border-cyan-200 bg-cyan-50 text-cyan-950' }, EXAMPLE: { icon: MessageSquareText, tone: 'border-violet-200 bg-violet-50 text-violet-950' }, EXEMPLO: { icon: MessageSquareText, tone: 'border-violet-200 bg-violet-50 text-violet-950' } }[label] || { icon: ListChecks, tone: 'border-slate-200 bg-slate-50 text-slate-900' }; const Icon = meta.icon; return <div key={label} className={`flex gap-3 rounded-xl border p-3 ${meta.tone}`}><Icon className="mt-0.5 h-4 w-4 shrink-0" /><div><div className="text-[11px] font-semibold uppercase tracking-wide opacity-70">{label}</div><p className="mt-1 text-sm leading-6">{text.join(': ')}</p></div></div>; })}</div>}
              </div>
            );
          })}
        </section>
        )}

        {activeHubSection === 'launch-package' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">Launch package</div>
            <p className="mt-2 text-sm leading-6 text-slate-600">Your launch support level and scheduled preparation activities.</p>
            <div className="mt-4 grid gap-3 text-sm">
              <div><div className="text-xs font-semibold uppercase text-slate-500">Support level</div><div className="mt-1 capitalize text-slate-800">{client.launchPackage?.level || 'supported'} launch</div></div>
              <div><div className="text-xs font-semibold uppercase text-slate-500">Planning call</div><div className="mt-1 text-slate-800">{client.launchPackage?.planningCallDate ? formatDate(new Date(`${client.launchPackage.planningCallDate}T00:00:00`), content.locale) : content.copy.notSetYet}</div></div>
              <div><div className="text-xs font-semibold uppercase text-slate-500">Staff training</div><div className="mt-1 text-slate-800">{client.launchPackage?.staffTrainingDate ? formatDate(new Date(`${client.launchPackage.staffTrainingDate}T00:00:00`), content.locale) : content.copy.notSetYet}</div></div>
              <div><div className="text-xs font-semibold uppercase text-slate-500">Support window</div><div className="mt-1 text-slate-800">{client.launchPackage?.supportStart || content.copy.notSetYet}{client.launchPackage?.supportEnd ? ` to ${client.launchPackage.supportEnd}` : ''}</div></div>
            </div>
          </div>
          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">Launch materials</div>
            <div className="mt-4 grid gap-2">
              {[['launchPlanUrl', 'Client launch plan'], ['staffCheatSheetUrl', 'Staff cheat sheet'], ['promotionKitUrl', 'Promotion kit'], ['technicalTestUrl', 'Technical readiness test'], ['supportProtocolUrl', 'Launch-day support protocol'], ['postLaunchReviewUrl', 'Post-launch review']].filter(([key]) => client.launchPackage?.[key]).map(([key, label]) => <a key={key} href={client.launchPackage[key]} target="_blank" rel="noreferrer" className="inline-flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-3 text-sm text-slate-800 hover:bg-slate-50"><span>{label}</span><ExternalLink className="h-4 w-4 text-slate-500" /></a>)}
              {!['launchPlanUrl', 'staffCheatSheetUrl', 'promotionKitUrl', 'technicalTestUrl', 'supportProtocolUrl', 'postLaunchReviewUrl'].some((key) => client.launchPackage?.[key]) && <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">Launch materials are not linked yet.</div>}
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'launch' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className={`${PANEL_CLASS} lg:col-span-2`}>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="text-lg font-semibold text-slate-900">{launchPromotionCopy.title}</div>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">{launchPromotionCopy.intro}</p>
              </div>
              {client.launchPackage?.promotionKitUrl && <a href={client.launchPackage.promotionKitUrl} target="_blank" rel="noreferrer" className={`${BUTTON_PRIMARY_CLASS} gap-2`}><FileText className="h-4 w-4" /> Open promotion kit <ExternalLink className="h-3.5 w-3.5" /></a>}
            </div>
            <div className="mt-5">
              <MediaLaunchTimeline
                compact
                items={client.mediaPackage?.packageData?.config?.promotion_calendar}
                launchDate={client.mediaPackage?.packageData?.config?.launch_date || client.agreedLaunchDate}
                language={language}
                partnerHandle={client.mediaPackage?.packageData?.config?.partner_social_handle || ''}
                completionById={Object.fromEntries((client.promotionPlan?.tasks || []).map((task) => [task.id, Boolean(task.completed || task.status === 'done')]))}
                updatingId={promotionUpdateTaskId}
                onToggle={handlePromotionTaskUpdate}
              />
            </div>
            {promotionUpdateStatus && <div className="mt-3 text-xs text-slate-500">{promotionUpdateStatus}</div>}
          </div>
          <div className="hidden">
            <div className="text-sm font-semibold text-slate-900">Launch ownership</div>
            <p className="mt-1 text-sm text-slate-500">Each item has a named owner and can include delivery evidence.</p>
            <div className="mt-4 grid gap-2">
              {LAUNCH_RESPONSIBILITIES.map((item) => {
                const value = client.launchResponsibilities?.[item.id] || {};
                return <div key={item.id} className="flex items-start gap-3 rounded-md border border-slate-200 px-3 py-3 text-sm">
                  {value.completed ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />}
                  <div className="min-w-0"><div className="font-medium text-slate-800">{item.label}</div><div className="mt-1 text-xs text-slate-500">{value.person || 'Owner not assigned'}</div>{value.evidenceUrl && <a href={value.evidenceUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-slate-900 underline">Open evidence <ExternalLink className="h-3 w-3" /></a>}</div>
                </div>;
              })}
            </div>
          </div>
          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">After launch</div>
            <div className="mt-4 grid gap-4">
              <div><div className="text-xs font-semibold uppercase text-slate-500">Follow-up</div><div className="mt-1 text-sm text-slate-800">{client.postLaunch?.followUpDate ? formatDate(new Date(`${client.postLaunch.followUpDate}T00:00:00`), content.locale) : content.copy.notSetYet} <span className="text-slate-500">({client.postLaunch?.followUpStatus || 'planned'})</span></div></div>
              <div><div className="text-xs font-semibold uppercase text-slate-500">Maintenance and support</div><p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-700">{client.postLaunch?.supportTerms || content.copy.notSetYet}</p></div>
              {client.postLaunch?.followUpNotes && <div><div className="text-xs font-semibold uppercase text-slate-500">Follow-up notes</div><p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-700">{client.postLaunch.followUpNotes}</p></div>}
              <div className="flex items-center gap-2 text-sm">{client.postLaunch?.analyticsReview ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-400" />} Analytics review included / completed</div>
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'payments' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className={PANEL_CLASS}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">{content.copy.paymentsTitle}</div>
                <p className="mt-1 text-sm text-slate-500">{content.copy.paymentsHelp}</p>
              </div>
              <WalletCards className="h-5 w-5 text-slate-500" />
            </div>
            {visiblePaymentItems.length > 0 && (
              <div className="mt-4 rounded-2xl border border-slate-200 p-4">
                <div className="text-sm font-semibold text-slate-900">
                  {showContractSteps && showPaymentSteps ? content.copy.contractPayment : showContractSteps ? content.copy.contractOnly : content.copy.paymentOnly}
                </div>
                <div className="mt-1 text-sm text-slate-500">
                  {showContractSteps && showPaymentSteps ? content.copy.contractPaymentHelp : showContractSteps ? content.copy.contractOnlyHelp : content.copy.paymentOnlyHelp}
                </div>
                <div className="mt-3 grid gap-2">
                  {visiblePaymentItems.map((item) => {
                    const checked = Boolean(client.payments?.[item.id]);
                    return <div key={item.id} className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-3 text-sm">{checked ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-400" />}<span className="text-slate-800">{item.label}</span><span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium capitalize text-slate-600">{item.type}</span></div>;
                  })}
                </div>
              </div>
            )}
            <div className="mt-4 grid gap-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">GuideSofia payment data</div>
                <div className="mt-3 grid gap-2 text-sm">
                  <div><span className="font-medium text-slate-700">Entity:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.entity}</div>
                  <div><span className="font-medium text-slate-700">Address:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.addressLine1}</div>
                  <div className="pl-12 text-slate-700">{GUIDE_SOFIA_PAYMENT_DETAILS.addressLine2}</div>
                  <div><span className="font-medium text-slate-700">Tax number:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.taxNumber}</div>
                  <div><span className="font-medium text-slate-700">Email:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.email}</div>
                  <div><span className="font-medium text-slate-700">Bank:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.bankName}</div>
                  <div><span className="font-medium text-slate-700">IBAN:</span> {GUIDE_SOFIA_PAYMENT_DETAILS.iban}</div>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 px-3 py-3">
                <div className="text-xs font-semibold uppercase text-slate-500">{content.copy.paymentStatus}</div>
                <div className="mt-1 text-sm text-slate-800">{client.paymentStatus || 'pending'}</div>
              </div>
              <div className={`rounded-2xl border px-3 py-3 ${client.paymentComplete ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                <div className="text-xs font-semibold uppercase text-slate-500">{content.copy.paymentComplete}</div>
                <div className={`mt-1 text-sm font-medium ${client.paymentComplete ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {client.paymentComplete ? content.copy.paymentCompleteYes : content.copy.paymentCompleteNo}
                </div>
              </div>
              {!showPaymentSteps && (
                <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
                  {content.copy.paymentOnlyHelp}
                </div>
              )}
            </div>
          </div>

          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">Payment summary</div>
            <div className="mt-4 grid gap-2">
              {translatedPaymentItems.map((item) => {
                const checked = Boolean(client.payments?.[item.id]);
                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 rounded-2xl border px-3 py-3 text-sm ${
                      checked ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'
                    }`}
                  >
                    {checked ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-400" />}
                    <span className="text-slate-800">{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'contact' && (
        <section className="grid gap-5 lg:grid-cols-3">
          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">{content.copy.contactTitle}</div>
            <p className="mt-1 text-sm text-slate-500">{content.copy.contactHelp}</p>
            <div className="mt-4 grid gap-3 text-sm">
              <div className="rounded-md border border-slate-200 px-3 py-3">
                <div className="text-xs font-semibold uppercase text-slate-500">{content.copy.projectManager}</div>
                <div className="mt-1 text-slate-900">{client.projectManagerName || content.copy.notSetYet}</div>
              </div>
              <div className="rounded-md border border-slate-200 px-3 py-3">
                <div className="text-xs font-semibold uppercase text-slate-500">{content.copy.email}</div>
                {client.projectManagerEmail ? (
                  <a href={`mailto:${client.projectManagerEmail}`} className="mt-1 inline-flex text-slate-900 underline underline-offset-2">
                    {client.projectManagerEmail}
                  </a>
                ) : (
                  <div className="mt-1 text-slate-500">{content.copy.notSetYet}</div>
                )}
              </div>
              <div className="rounded-md border border-slate-200 px-3 py-3">
                <div className="text-xs font-semibold uppercase text-slate-500">{content.copy.whatsapp}</div>
                {client.projectManagerWhatsapp ? (
                  <a href={`https://wa.me/${client.projectManagerWhatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex text-slate-900 underline underline-offset-2">
                    {client.projectManagerWhatsapp}
                  </a>
                ) : (
                  <div className="mt-1 text-slate-500">{content.copy.notSetYet}</div>
                )}
              </div>
              {guideSofiaAdditionalContacts.map((contact, index) => (
                <div key={`gs-extra-${index}`} className="rounded-md border border-slate-200 px-3 py-3">
                  <div className="text-xs font-semibold uppercase text-slate-500">{content.copy.additionalContactLabel || 'Additional contact'}</div>
                  <div className="mt-1 text-slate-900">{contact.name || content.copy.notSetYet}</div>
                  {contact.email ? (
                    <a href={`mailto:${contact.email}`} className="mt-1 block text-slate-900 underline underline-offset-2">
                      {contact.email}
                    </a>
                  ) : null}
                  {contact.phone ? (
                    <a href={`https://wa.me/${contact.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="mt-1 block text-slate-900 underline underline-offset-2">
                      {contact.phone}
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">{content.copy.clientContactTitle}</div>
            <p className="mt-1 text-sm text-slate-500">{content.copy.clientContactHelp}</p>
            <div className="mt-4 grid gap-3 text-sm">
              <div className="rounded-md border border-slate-200 px-3 py-3">
                <div className="text-xs font-semibold uppercase text-slate-500">{content.copy.contactName}</div>
                <div className="mt-1 text-slate-900">{client.clientContactName || content.copy.notSetYet}</div>
              </div>
              <div className="rounded-md border border-slate-200 px-3 py-3">
                <div className="text-xs font-semibold uppercase text-slate-500">{content.copy.email}</div>
                {client.clientContactEmail ? (
                  <a href={`mailto:${client.clientContactEmail}`} className="mt-1 inline-flex text-slate-900 underline underline-offset-2">
                    {client.clientContactEmail}
                  </a>
                ) : (
                  <div className="mt-1 text-slate-500">{content.copy.notSetYet}</div>
                )}
              </div>
              <div className="rounded-md border border-slate-200 px-3 py-3">
                <div className="text-xs font-semibold uppercase text-slate-500">{content.copy.whatsapp}</div>
                {client.clientContactWhatsapp ? (
                  <a href={`https://wa.me/${client.clientContactWhatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex text-slate-900 underline underline-offset-2">
                    {client.clientContactWhatsapp}
                  </a>
                ) : (
                  <div className="mt-1 text-slate-500">{content.copy.notSetYet}</div>
                )}
              </div>
              {clientAdditionalContacts.map((contact, index) => (
                <div key={`client-extra-${index}`} className="rounded-md border border-slate-200 px-3 py-3">
                  <div className="text-xs font-semibold uppercase text-slate-500">{content.copy.additionalContactLabel || 'Additional contact'}</div>
                  <div className="mt-1 text-slate-900">{contact.name || content.copy.notSetYet}</div>
                  {contact.email ? (
                    <a href={`mailto:${contact.email}`} className="mt-1 block text-slate-900 underline underline-offset-2">
                      {contact.email}
                    </a>
                  ) : null}
                  {contact.phone ? (
                    <a href={`https://wa.me/${contact.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="mt-1 block text-slate-900 underline underline-offset-2">
                      {contact.phone}
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">{content.copy.meetingTitle}</div>
            <p className="mt-1 text-sm text-slate-500">{content.copy.meetingHelp}</p>
            <div className="mt-4">
              {client.meetingLink ? (
                <a
                  href={client.meetingLink}
                  target="_blank"
                  rel="noreferrer"
                  className={`${BUTTON_PRIMARY_CLASS} w-full`}
                >
                  {content.copy.openBookingLink}
                </a>
              ) : (
                <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
                  {content.copy.bookingMissing}
                </div>
              )}
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'roles' && (
        <section className="grid gap-5 lg:grid-cols-2">
          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">{content.copy.gsResponsibilities}</div>
            <div className="mt-4 grid gap-2">
              {content.guideSofiaResponsibilities.map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-2xl border border-slate-200 px-3 py-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-blue-600" />
                  <span className="text-slate-800">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={PANEL_CLASS}>
            <div className="text-sm font-semibold text-slate-900">{content.copy.clientResponsibilities}</div>
            <div className="mt-4 grid gap-2">
              {content.clientResponsibilities.map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-2xl border border-slate-200 px-3 py-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="text-slate-800">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
        )}

        {activeHubSection === 'faq' && (
        <section className={PANEL_CLASS}>
          <div className="text-sm font-semibold text-slate-900">{content.copy.faqTitle}</div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {content.faq.map((item) => (
              <div key={item.question} className="rounded-md border border-slate-200 p-4">
                <div className="text-sm font-medium text-slate-900">{item.question}</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">{item.answer}</p>
              </div>
            ))}
          </div>
        </section>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClientOnboardingPublicPage;
